<script setup lang="ts">
import {
  useBrowserStore,
  useItineraryStore,
  usePaxStore,
  usePriceCalendarStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { TabKey } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore.model';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';

import { AppPortableText } from '@libs/ui';

import { PremadeTripType } from '../../models';
import {
  usePremadeTripStore,
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
} from '../../stores';

import AITripDescription from './AITripDescription.vue';
import PremadeTripHeaderDesktop from './PremadeTripHeaderDesktop.vue';
import PremadeTripHeaderMobile from './PremadeTripHeaderMobile.vue';
import TripOverviewHeaderActions from './TripOverviewHeaderActions.vue';

const tripOverviewPageStore = useTripOverviewPageStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const mapStore = useMapStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();

const premadeTripStore = usePremadeTripStore();
const browserStore = useBrowserStore();
const paxStore = usePaxStore();
const statsigEventsStore = useStatsigEventsStore();
const priceCalendarStore = usePriceCalendarStore();
const itineraryStore = useItineraryStore();

interface TripOverviewHeaderProps {
  printMode: boolean;
}

const props = defineProps<TripOverviewHeaderProps>();

function openPaxSelectorModal(): void {
  tripOverviewPanelsStore.openConfigurationPanel(TabKey.Pax);
}

function openEditTripModal(): void {
  tripOverviewPanelsStore.openConfigurationPanel(TabKey.Itinerary);
}

function onHomeAirportClick(): void {
  tripOverviewPanelsStore.openConfigurationPanel(TabKey.Airplane);
  statsigEventsStore.logTripOverviewFlyFromButtonClicked();
}

function openMonthsSelectorModal(): void {
  tripOverviewPanelsStore.openConfigurationPanel(TabKey.Calendar);
}

function openDynamicFullScreenMap(): void {
  mapStore.openFullScreenDynamicTripMap();
}
</script>

<template>
  <template v-if="premadeTripStore.showingPremadeTrip">
    <PremadeTripHeaderMobile
      v-if="browserStore.isSmallerThan1200px && premadeTripStore.premadeTrip"
      :trip-details="premadeTripStore.premadeTrip"
      @edit-trip="openEditTripModal()"
      @show-full-description="
        tripOverviewModalsStore.openPremadeTripDetailsModal
      "
    >
      <template #description>
        <AppPortableText
          v-if="
            premadeTripStore.premadeTrip.type ===
            PremadeTripType.PublishedInSanity
          "
          :portable-text="premadeTripStore.premadeTrip.description"
          type="premade-trip"
        />
        <AITripDescription
          v-if="premadeTripStore.premadeTrip.type === PremadeTripType.AI"
          :description="premadeTripStore.premadeTrip.description"
        />
      </template>
    </PremadeTripHeaderMobile>
    <PremadeTripHeaderDesktop
      v-if="!browserStore.isSmallerThan1200px && premadeTripStore.premadeTrip"
      :trip-details="premadeTripStore.premadeTrip"
      @edit-trip="openEditTripModal()"
      @show-full-description="
        tripOverviewModalsStore.openPremadeTripDetailsModal
      "
    >
      <template #description>
        <AppPortableText
          v-if="
            premadeTripStore.premadeTrip.type ===
            PremadeTripType.PublishedInSanity
          "
          :portable-text="premadeTripStore.premadeTrip.description"
          type="premade-trip"
          class="description"
        />
        <AITripDescription
          v-if="premadeTripStore.premadeTrip.type === PremadeTripType.AI"
          :description="premadeTripStore.premadeTrip.description"
        />
      </template>
    </PremadeTripHeaderDesktop>
  </template>

  <div
    v-if="
      !browserStore.isSmallerThan1200px && premadeTripStore.showingPremadeTrip
    "
    class="separator with-side-margin"
  />

  <div v-if="!props.printMode" class="actions">
    <TripOverviewHeaderActions
      :paxes-button-text="paxStore.getPaxesButtonDetailedText"
      :home-flight-data="tripOverviewPageStore.homeFlightData"
      :trip-start-date="itineraryStore.tripStartDate"
      :number-of-destinations="tripOverviewPageStore.numberOfDestinations"
      :number-of-nights="tripOverviewPageStore.numberOfNights"
      :price-calendar-button-text="priceCalendarStore.calendarTriggerText"
      :first-destination-city-name="
        tripOverviewPageStore.getFirstDestinationCityName()
      "
      :is-user-selected-itinerary="
        itineraryStore.userSelectedItinerary !== undefined
      "
      @select-month="priceCalendarStore.selectItineraryMonth"
      @pax-click="openPaxSelectorModal"
      @home-airport-click="onHomeAirportClick"
      @edit-trip="openEditTripModal()"
      @months-selector-click="openMonthsSelectorModal()"
      @map-click="openDynamicFullScreenMap"
    />
  </div>

  <div
    v-if="
      browserStore.isSmallerThan1200px && premadeTripStore.showingPremadeTrip
    "
    class="separator"
  />
</template>

<style scoped lang="scss">
.actions {
  padding: 0 var(--spacing-24) var(--spacing-8) var(--spacing-24);
  background-color: var(--color-white);
  @include for-less-or-equal-than-599-px {
    padding: 0 var(--spacing-16) var(--spacing-16) var(--spacing-24);
  }
}

.separator {
  border-bottom: 1px solid var(--color-secondary-90);
  margin-bottom: var(--spacing-8);

  &.with-side-margin {
    margin-left: var(--spacing-24);
    margin-right: var(--spacing-24);
  }
}
</style>
